import React from 'react';
import {
  Box,
  Card,
  Paper,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Button,
  Chip,
  Typography,
  LinearProgress,
  IconButton,
  Tooltip,
  Collapse,
  Divider,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import FilterListIcon from '@mui/icons-material/FilterList';
import Radio from '@mui/material/Radio';
import Autocomplete from '@mui/material/Autocomplete';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import { flushSync } from 'react-dom';

import Client from '../Client';
import Config from '../ClientConfig';
import BackofficeReportsApi from './backofficeReportsApi';

/**
 * SalesCountOrders – Smart, compact report page (v2)
 * - Sticky filter bar + sticky totals summary cards (orders, total, %) with progress
 * - Fixed-width, stable-height Autocompletes with radio-look options
 * - Enforce MAX 3 selections across Products + Categories + Subcategories (but Subcat options are NOT filtered by selected Categories)
 * - Totals section now mirrors per-location metrics (incl. %)
 * - Shorter labels + narrower Payment/Amount fields; amount must be > 0
 * - CSV includes totals incl. overall %
 * - Quick date presets (Today, 7d, 30d)
 * - Empty state + inline Retry on error
 * - Filter persistence to LocalStorage
 */

function createOrdersWorker() {
  const code = `
    self.onmessage = (e) => {
      const { payload } = e.data;
      try {
        const raw = payload?.entities ?? payload ?? [];
        const rows = Array.isArray(raw) ? raw.slice() : [];
        // Sort by location_name (same logic you use in the UI)
        rows.sort((a,b)=> (a?.location_name||'').localeCompare(b?.location_name||'', undefined, { sensitivity:'base' }));

        // Totals (mirror your computeTotalsFromRows)
        const sum = (arr, key) => arr.reduce((acc, r) => acc + (Number(r?.[key]) || 0), 0);
        const orders = sum(rows, 'orders');
        const base = sum(rows, 'location_total_orders');
        const percent = base ? (orders / base) * 100 : 0;

        postMessage({ rows, totals: { orders, location_total_orders: base, percent } });
      } catch (err) {
        postMessage({ error: String(err && err.message || err) });
      }
    };
  `;
  const blob = new Blob([code], { type: 'text/javascript' });
  return new Worker(URL.createObjectURL(blob));
}

const paymentOptions = [
  { value: 0, label: 'Card' },
  { value: 1, label: 'Cash' },
  { value: 2, label: 'Bank Transfer' },
  { value: 3, label: 'Credit' },
  { value: 6, label: 'Voucher' },
];

const deliveryOptions = [
  { value: 0, label: 'PickUp' },
  { value: 3, label: 'Glovo' },
  { value: 4, label: 'Wolt' },
  { value: 5, label: 'Bolt' },
];

const paymentTypeLabels = {
  0: "Cash",
  1: "Card",
  2: "MealTicket",
  3: "Bank",
  6: "Other"
};

const deliveryTypeLabels = {
  0: "PickUp",
  3: "Glovo",
  4: "Wolt",
  5: "Bolt"
};

const amountOpLabels = {
  gt: 'Greater than',
  lt: 'Less than',
  eq: 'Equal to'
};

class SalesCountOrders extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      client: new Client('none'),
      rows: [],
      page: 0,
      rowsPerPage: -1,
      loading: false,
      // filters
      dateFrom: '',
      dateTo: '',
      amount_op: '', // required iff amount_value set
      amount_value: '',
      payment_types: [], // [0,1,2,3,6]
      delivery_types: [], // NEW: [0,3,4,5]
      // dropdown (arrays of {label, value})
      products: [],
      categories: [],
      subcategories: [],
      // server echo (may be empty; we will compute client-side totals as fallback)
      totals: {},
      error: '',
      // ui
      filtersOpen: true,
      amountError: '',
      dateError: '',
      // option sources (NOT filtered by category for Subcats)
      productOptions: [],
      categoryOptions: [],
      subcategoryOptions: [],
      // subtle hint/snackbar
      hintOpen: false,
      hintMsg: '',
      busyOverlay: false,
    };

    this.getData = this.getData.bind(this);
    this.handleChangePage = this.handleChangePage.bind(this);
    this.handleChangeRowsPerPage = this.handleChangeRowsPerPage.bind(this);
    this.exportCSV = this.exportCSV.bind(this);
    this.resetFilters = this.resetFilters.bind(this);
    this.toggleFilters = this.toggleFilters.bind(this);

    this.onProductsChange = this.onProductsChange.bind(this);
    this.onCategoriesChange = this.onCategoriesChange.bind(this);
    this.onSubcategoriesChange = this.onSubcategoriesChange.bind(this);

    this.notify = this.notify.bind(this);
    this._reqSeq = 0; 
    this._isLoading = false;
    this._overlayShownAt = 0;
  }

  componentDidMount() {
    // Restore ONLY the selected period if available; no other filters from localStorage
    /* let cached = null;
    try { cached = JSON.parse(localStorage.getItem('orders_report_filters_period')||'null'); } catch(_e){}

    if (cached?.dateFrom && cached?.dateTo) {
      this.setState({ dateFrom: cached.dateFrom, dateTo: cached.dateTo }, () => {
        this.loadDropdowns().finally(this.getData);
      });
      return;
    } */

    // default to last 7 days
    const today = new Date();
    const toISO = (d) => d.toISOString().slice(0,10);
    const dTo = toISO(today);
    const dFrom = toISO(new Date(today.getTime() - 0*86400000));
    this.setState({ dateFrom: dFrom, dateTo: dTo }, () => {
      this.loadDropdowns().finally(this.getData);
    });
  }

  componentWillUnmount() {
    clearTimeout(this._hintTimer);
  }

  componentDidUpdate(_prevProps, prevState) {
    // Persist ONLY the period (dateFrom/dateTo)
    if (this.state.dateFrom !== prevState.dateFrom || this.state.dateTo !== prevState.dateTo) {
      const payload = { dateFrom: this.state.dateFrom, dateTo: this.state.dateTo };
      // try { localStorage.setItem('orders_report_filters_period', JSON.stringify(payload)); } catch(_e){}
    }
  }

  // --- Data loading for dropdowns ---
  async loadDropdowns() {
    const client = this.state.client;
    const FILTER = { isForSale: 1, orderBy_name: true };

    // PRODUCTS (isForSale:1, orderBy_name:true)
    try {
      const PRODUCTS_ENDPOINT = (Config.API_URL_PRODUCT + 'list_products/');
      const productsResp = await client.getData(PRODUCTS_ENDPOINT, FILTER) || {};
      const list = (productsResp?.products) || (productsResp?.data) || (Array.isArray(productsResp) ? productsResp : []);
      const productOptions = list.map((p) => ({ label: p.name || p.iref, value: p.iref }));
      this.setState({ productOptions });
    } catch(_e) {}

    // CATEGORIES via client.getProductsCategories()
    try {
      const catData = this.state.client.getProductsCategories?.(FILTER);
      const cats = catData?.productCategories || [];
      const categoryOptions = cats.map(c => ({ label: c.name || c.iref, value: c.iref }));
      this.setState({ categoryOptions });
    } catch(_e) {}

    // SUBCATEGORIES: load ALL available (NOT filtered by selected categories)
    try {
      let merged = [];
      if (this.state.client.getProductsSubCategories) {
        const direct = this.state.client.getProductsSubCategories(FILTER);
        if (direct && Array.isArray(direct.productSubCategories)) {
          merged = direct.productSubCategories;
        } else {
          // Fallback: iterate over all categories and merge uniques
          const cats = this.state.categoryOptions || [];
          const seen = new Set();
          for (const c of cats) {
            try {
              const data = this.state.client.getProductsSubCategories({ ...FILTER, category: c.value });
              const arr = data?.productSubCategories || [];
              for (const s of arr) {
                if (seen.has(s.iref)) continue;
                seen.add(s.iref);
                merged.push(s);
              }
            } catch(_ee) { /* ignore per cat */ }
          }
        }
      }
      const subcategoryOptions = merged.map(s => ({ label: s.name || s.iref, value: s.iref }));
      this.setState({ subcategoryOptions });
    } catch(_e) { this.setState({ subcategoryOptions: [] }); }
  }

  toggleFilters() { this.setState({ filtersOpen: !this.state.filtersOpen }); }

  notify(msg) {
    if (!msg) return;
    this.setState({ hintOpen: true, hintMsg: msg }, () => {
      clearTimeout(this._hintTimer);
      this._hintTimer = setTimeout(() => this.setState({ hintOpen: false }), 2000);
    });
  }

  // MAX selections across all dropdowns = 3
  getRemainingSlots() {
    const count = (this.state.products?.length||0) + (this.state.categories?.length||0) + (this.state.subcategories?.length||0);
    return Math.max(0, 5 - count);
  }

  onProductsChange(_, val) {
    const allowed = this.getRemainingSlots() + (this.state.products?.length||0);
    if ((val||[]).length > allowed) this.notify('Max 5 total across Prod./Cat./Subcat.');
    this.setState({ products: (val||[]).slice(0, allowed) });
  }

  onCategoriesChange(_, val) {
    const allowed = this.getRemainingSlots() + (this.state.categories?.length||0);
    if ((val||[]).length > allowed) this.notify('Max 5 total across Prod./Cat./Subcat.');
    this.setState({ categories: (val||[]).slice(0, allowed) });
    // do NOT filter subcategory options by selected categories
  }

  onSubcategoriesChange(_, val) {
    const allowed = this.getRemainingSlots() + (this.state.subcategories?.length||0);
    if ((val||[]).length > allowed) this.notify('Max 5 total across Prod./Cat./Subcat.');
    this.setState({ subcategories: (val||[]).slice(0, allowed) });
  }

  percentColor(v) {
    const x = Number(v) || 0;
    if (x >= 70) return 'success';
    if (x >= 30) return 'warning';
    return 'error';
  }

  hasDeliveryFilter() {
    const dt = this.state?.delivery_types || [];
    return Array.isArray(dt) && dt.length > 0;
  }

  activeFiltersCount() {
    const { amount_op, amount_value, payment_types, delivery_types, products, categories, subcategories } = this.state;
    let n = 0;

    // Amount group counts if both op+value are valid and > 0
    if (amount_op && amount_value !== '' && Number(amount_value) > 0) n += 1;

    // Payment group counts as 1 if anything selected
    if ((payment_types?.length || 0) > 0) n += 1;

    if ((delivery_types?.length || 0) > 0) n += 1;

    // Product/category/subcategory groups: count each group once if any selected
    if ((products?.length || 0) > 0) n += 1;
    if ((categories?.length || 0) > 0) n += 1;
    if ((subcategories?.length || 0) > 0) n += 1;

    return n;
  }

  // Compute totals client-side from current rows (fallback if backend totals are 0/undefined)
  computeTotalsFromRows(rows) {
    const sum = (arr, key) => arr.reduce((acc, r) => acc + (Number(r?.[key]) || 0), 0);
    const orders = sum(rows, 'orders');
    const base = sum(rows, 'location_total_orders');
    const pct = base ? (orders / base) * 100 : 0;
    return { orders, location_total_orders: base, percent: pct };
  }

  overallPct() {
    const computed = this.computeTotalsFromRows(this.state.rows);
    const backend = this.state.totals || {};

    const periodTotal =
      Number(backend.orders_period_all) ||
      (Number.isFinite(computed.location_total_orders) ? computed.location_total_orders : 0);

    const filteredOrders =
      Number(backend.orders_filtered) ||
      Number(backend.orders) ||
      computed.orders || 0;

    const pct = periodTotal ? (filteredOrders / periodTotal) * 100 : 0;
    return Math.max(0, Math.min(100, pct));
  }

  buildQuery() {
    const p = new URLSearchParams();
    p.set('dateFrom', this.state.dateFrom);
    p.set('dateTo', this.state.dateTo);

    // --- Date range validation (max 40 days) ---
    const from = new Date(this.state.dateFrom);
    const to = new Date(this.state.dateTo);
    const diffDays = isFinite(from) && isFinite(to) ? Math.floor((to - from) / 86400000) : 0;
    const dateError = (isNaN(diffDays) || diffDays < 0) ? 'Invalid dates' : (diffDays > 40 ? 'Max 40 days' : '');
    if (this.state.dateError !== dateError) this.setState({ dateError });

    // Amount validation: op required if value set, and value required if op set + value must be > 0
    const rawVal = this.state.amount_value;
    const valueSet = rawVal !== '' && rawVal !== null;
    const opSet = !!this.state.amount_op;
    const numericVal = Number(rawVal);
    let amountError = '';
    if (valueSet && !opSet) amountError = 'Choose op';
    else if (opSet && !valueSet) amountError = 'Enter value';
    else if (valueSet && !(numericVal > 0)) amountError = 'Must be > 0';
    if (this.state.amountError !== amountError) this.setState({ amountError });

    if (opSet && valueSet && numericVal > 0) {
      p.set('amount_op', this.state.amount_op);
      p.set('amount_value', String(numericVal));
    }

    if (this.state.payment_types && this.state.payment_types.length) {
      p.set('payment_types', this.state.payment_types.join(','));
    }

    if (this.state.delivery_types && this.state.delivery_types.length) {
      p.set('delivery_types', this.state.delivery_types.join(','));
    }

    // cap to max 5 across all (products -> categories -> subcategories)
    const prodValsAll = (this.state.products || []).map(o => o.value);
    const catValsAll  = (this.state.categories || []).map(o => o.value);
    const subValsAll  = (this.state.subcategories || []).map(o => o.value);

    let remaining = 5;
    const prodVals = prodValsAll.slice(0, remaining);
    remaining -= prodVals.length;

    const catVals  = catValsAll.slice(0, Math.max(0, remaining));
    remaining -= catVals.length;

    const subVals  = subValsAll.slice(0, Math.max(0, remaining));

    if (prodVals.length) p.set('products', prodVals.join(','));
    if (catVals.length)  p.set('categories', catVals.join(','));
    if (subVals.length)  p.set('subcategories', subVals.join(','));

    // Always exclude zero-locations
    p.set('include_zero', 'true');

    return p.toString();
  }

  async getData() {
    // block apply when amount invalid or date invalid
    const from = new Date(this.state.dateFrom);
    const to = new Date(this.state.dateTo);
    const diffDays = isFinite(from) && isFinite(to) ? Math.floor((to - from) / 86400000) : 0;
    const dateInvalid = isNaN(diffDays) || diffDays < 0 || diffDays > 40;
    const rawVal = this.state.amount_value;
    const numericVal = Number(rawVal);
    const amountInvalid = (rawVal !== '' && !this.state.amount_op) || (this.state.amount_op && rawVal === '') || (rawVal !== '' && !(numericVal > 0));
    if (dateInvalid || amountInvalid) {
      const parts = [];
      if (dateInvalid) parts.push(diffDays > 40 ? 'Window must be ≤ 40 days' : 'Invalid date range');
      if (amountInvalid) parts.push(rawVal !== '' && !(numericVal > 0) ? 'Amount must be > 0' : 'Select amount op and value');
      this.setState({ dateError: dateInvalid ? (diffDays > 40 ? 'Max 40 days' : 'Invalid dates') : '', amountError: amountInvalid ? (rawVal !== '' && !(numericVal > 0) ? 'Must be > 0' : 'Complete both fields') : '' });
      this.notify(parts.join(' • '));
      return;
    }

    this._isLoading = true;
    this._overlayShownAt = Date.now();
    // Close any poppers/tooltips that could overlap the overlay
    try { document.activeElement?.blur?.(); } catch {}
    // Show overlay immediately and commit it synchronously

    flushSync(() => {
      this.setState({ loading: true, busyOverlay: true, hintOpen: false, error: '' });
    });

    // let React commit + paint
    await new Promise(requestAnimationFrame);
    // optional extra yield for Safari/Chrome consistency
    await new Promise(requestAnimationFrame);

    // mark when overlay is actually visible
    this._overlayShownAt = performance.now();
    // console.log('overlay should be visible now?', this.state.busyOverlay);

    try {
      const query = this.buildQuery();
      const endpoint = BackofficeReportsApi.getOrdersByLocationUrl();

      let data = null;
      if (this.state.client && this.state.client.getData) {
        const queryObj = Object.fromEntries(new URLSearchParams(query));
        // If client path has sync work, yield a microtask
        await Promise.resolve();
        data = await BackofficeReportsApi.getOrdersByLocation(this.state.client, queryObj);
      }
      if (!data) { // fallback fetch if client returned null/undefined
        const url = `${endpoint}?${query}`;
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        data = await res.json();
      }
      const payload = data && data.entities ? data.entities : Array.isArray(data) ? data : [];
      // Small yield; keep main thread snappy even if worker spins up
      await Promise.resolve();
      const worker = createOrdersWorker();
      const { rows, totals, error } = await new Promise((resolve) => {
        worker.onmessage = (e) => { worker.terminate(); resolve(e.data); };
        worker.onerror = (e) => { worker.terminate(); resolve({ error: e.message || 'Worker error' }); };
        worker.postMessage({ payload });
      });
      if (error) throw new Error(error);
      const computed = this.computeTotalsFromRows(rows);
      const mergedTotals = {
        ...totals,                // worker/client computed fallback
        ...data?.totals           // prefer any backend fields present (orders_period_all, orders_period_delivery, orders_filtered, etc.)
      };
      this.setState({ rows, totals: mergedTotals, computedTotals: computed });
      this.setState({ rows, totals: mergedTotals, computedTotals: computed });
    } catch (e) {
      this.setState({ error: e.message || 'Failed to load' });
    } finally {
      this._isLoading = false;
      const MIN_VISIBLE = 400; // ms
      const shownFor = performance.now() - (this._overlayShownAt || 0);
      const hide = () => this.setState({ loading: false, busyOverlay: false });

      if (shownFor < MIN_VISIBLE) {
        setTimeout(hide, MIN_VISIBLE - shownFor);
      } else {
        hide();
      }
    }
  }

  exportCSV() {
    // Build CSV from current state (no extra network call)
    const {
      rows = [],
      dateFrom,
      dateTo,
      amount_op,
      amount_value,
      payment_types,
      delivery_types,
      products,
      categories,
      subcategories,
    } = this.state || {};

    const backend = this.state.totals || {};
    const filteredTotal = Number(backend.orders_filtered) || 0;
    const periodTotal  = Number(backend.orders_period_all) || 0;
    const pct = periodTotal ? (filteredTotal / periodTotal) * 100 : 0;

    // CSV helpers
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const safeForFilename = (s) => String(s ?? '').replace(/[\/:*?"<>|]/g, '-');

    // Header with filters
    const filtersHeader = [
      ['dateFrom', dateFrom],
      ['dateTo', dateTo],
      ['amount_op', amountOpLabels[amount_op] || amount_op],
      ['amount_value', amount_value],
      ['payment_types', (payment_types || [])
        .map(v => paymentTypeLabels[v] || v)
        .join('|')],
      ['delivery_types', (delivery_types || [])
        .map(v => deliveryTypeLabels[v] || v)
        .join('|')],
      ['products', (products || []).map(x => x.label).join('|')],
      ['categories', (categories || []).map(x => x.label).join('|')],
      ['subcategories', (subcategories || []).map(x => x.label).join('|')],
    ].map(([k, v]) => `${esc(k)},${esc(v)}`);

    const lines = [];
    lines.push('FILTER,VALUE');
    lines.push(...filtersHeader);
    lines.push('');

    // Totals section (computed)
    lines.push('TOTALS,VALUE');
    lines.push(`"Filtered Orders",${esc(filteredTotal)}`);
    lines.push(`"Total (period)",${esc(periodTotal)}`);
    lines.push(`"% of total",${esc(`${pct.toFixed(2)}%`)}`);
    lines.push('');

    lines.push('Location,Filtered Orders,Location Total,% of Location Total');

    for (const r of rows) {
      const cols = [
        r.location_name ?? '',
        r.orders ?? 0,
        r.location_total_orders ?? 0,
        r.percent_of_total ?? 0,
      ].map(esc);
      lines.push(cols.join(','));
    }

    const csv = "\uFEFF" + lines.join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `orders_by_location_${safeForFilename(dateFrom)}_${safeForFilename(dateTo)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }

  resetFilters() {
    const today = new Date();
    const toISO = (d) => d.toISOString().slice(0,10);
    const dTo = toISO(today);
    const dFrom = toISO(new Date(today.getTime() - 0*86400000));
    this.setState({
      dateFrom: dFrom,
      dateTo: dTo,
      amount_op: '',
      amount_value: '',
      payment_types: [],
      delivery_types: [],
      products: [],
      categories: [],
      subcategories: [],
    }, this.getData);
  }

  handleChangePage(event, newPage) { this.setState({ page: newPage }); }
  handleChangeRowsPerPage(event) { this.setState({ rowsPerPage: +event.target.value, page: 0 }); }

  // --- UI helpers ---
  renderFilterBar() {
    const { filtersOpen } = this.state;
    const from = new Date(this.state.dateFrom);
    const to = new Date(this.state.dateTo);
    const diffDays = isFinite(from) && isFinite(to) ? Math.floor((to - from) / 86400000) : 0;
    const dateInvalid = isNaN(diffDays) || diffDays < 0 || diffDays > 40;

    const rawVal = this.state.amount_value;
    const numericVal = Number(rawVal);
    const amountInvalid =
      (rawVal !== '' && !this.state.amount_op) ||
      (this.state.amount_op && rawVal === '') ||
      (rawVal !== '' && !(numericVal > 0));

    const disableApply = dateInvalid || amountInvalid;
    const activeFilters = this.activeFiltersCount();

    return (
      <Box
        sx={{
          px: { xs: 0.75, sm: 1 },
          py: { xs: 0.65, sm: 0.85 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: { xs: 0.55, sm: 1 },
          flexWrap: 'nowrap',
          flexShrink: 0,
          borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
          bgcolor: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
          position: 'sticky',
          top: 0,
          zIndex: 4,
        }}
      >
        <Box
          sx={{
            minWidth: 0,
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 0.5, sm: 0.75 },
            overflow: 'hidden',
          }}
        >
          <Typography
            sx={{
              display: { xs: 'none', sm: 'block' },
              color: '#1a237e',
              fontWeight: 800,
              fontSize: { sm: '0.92rem', md: '1rem' },
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            Orders by Location
          </Typography>

          <Tooltip title={filtersOpen ? 'Hide filters' : 'Show filters'}>
            <IconButton
              onClick={this.toggleFilters}
              size="small"
              color={activeFilters > 0 ? 'primary' : 'default'}
              sx={{
                width: 36,
                height: 36,
                flexShrink: 0,
                borderRadius: '12px',
                border: '1px solid rgba(26, 35, 126, 0.12)',
                color: activeFilters > 0 || filtersOpen ? '#1a237e' : '#64748b',
                bgcolor:
                  activeFilters > 0 || filtersOpen
                    ? 'rgba(26, 35, 126, 0.08)'
                    : '#fff',
                '&:hover': {
                  bgcolor: 'rgba(26, 35, 126, 0.12)',
                },
              }}
              aria-label={filtersOpen ? 'Hide filters' : 'Show filters'}
            >
              <FilterListIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Chip
            size="small"
            label={activeFilters > 0 ? `${activeFilters} applied` : 'No filters'}
            color={activeFilters > 0 ? 'primary' : 'default'}
            variant={activeFilters > 0 ? 'filled' : 'outlined'}
            onClick={!filtersOpen ? this.toggleFilters : undefined}
            sx={{
              height: 30,
              maxWidth: { xs: 108, sm: 140 },
              borderRadius: 999,
              fontWeight: 650,
              fontSize: { xs: '0.76rem', sm: '0.82rem' },
              '& .MuiChip-label': {
                px: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
            }}
          />
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 0.5, sm: 0.75 },
          }}
        >
          <Tooltip
            arrow
            placement="bottom-end"
            title={this.state.hintMsg || (disableApply ? 'Fix fields to apply' : 'Apply filters')}
            open={this.state.hintOpen}
            disableFocusListener
            disableTouchListener
            disableHoverListener
          >
            <span>
              <Button
                disabled={disableApply}
                variant="contained"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={this.getData}
                onMouseEnter={() => {
                  if (disableApply && !this.state.hintOpen) {
                    this.setState({ hintOpen: true, hintMsg: 'Fix fields to apply' });
                    clearTimeout(this._hintTimer);
                    this._hintTimer = setTimeout(() => this.setState({ hintOpen: false }), 1200);
                  }
                }}
                sx={{
                  height: 36,
                  borderRadius: '12px',
                  boxShadow: 'none',
                  textTransform: 'none',
                  fontWeight: 750,
                  px: { xs: 1, sm: 1.25 },
                  minWidth: { xs: 36, sm: 86 },
                  '& .MuiButton-startIcon': {
                    mr: { xs: 0, sm: 0.5 },
                  },
                  '& .MuiButton-startIcon + span': {
                    display: { xs: 'none', sm: 'inline' },
                  },
                }}
              >
                Apply
              </Button>
            </span>
          </Tooltip>

          <Tooltip title="Export CSV">
            <Button
              variant="contained"
              size="small"
              onClick={this.exportCSV}
              sx={{
                minWidth: 36,
                width: { xs: 36, sm: 'auto' },
                height: 36,
                px: { xs: 0, sm: 1.1 },
                flexShrink: 0,
                borderRadius: '12px',
                color: '#1a237e',
                bgcolor: 'rgba(26, 35, 126, 0.08)',
                boxShadow: 'none',
                border: '1px solid rgba(26, 35, 126, 0.12)',
                textTransform: 'none',
                fontWeight: 750,
                '&:hover': {
                  bgcolor: 'rgba(26, 35, 126, 0.12)',
                  boxShadow: 'none',
                },
                '& .MuiButton-startIcon': {
                  m: { xs: 0, sm: '0 4px 0 -2px' },
                },
              }}
              startIcon={<DownloadIcon fontSize="small" />}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                CSV
              </Box>
            </Button>
          </Tooltip>

          <Tooltip title="Reset filters">
            <Button
              variant="text"
              size="small"
              onClick={this.resetFilters}
              sx={{
                height: 36,
                minWidth: { xs: 36, sm: 68 },
                px: { xs: 0, sm: 1 },
                borderRadius: '12px',
                color: '#64748b',
                textTransform: 'none',
                fontWeight: 750,
                '& .MuiButton-startIcon': {
                  m: { xs: 0, sm: '0 4px 0 -2px' },
                },
              }}
              startIcon={<ClearAllIcon fontSize="small" />}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                Reset
              </Box>
            </Button>
          </Tooltip>
        </Box>
      </Box>
    );
  }

  renderFilters() {
    const { dateFrom, dateTo, amount_op, amount_value, payment_types, products, categories, subcategories, productOptions, categoryOptions, subcategoryOptions, amountError } = this.state;
    const totalSelected = (products?.length||0) + (categories?.length||0) + (subcategories?.length||0);
    const disableNewPicks = totalSelected >= 5;

    const normalize = (s='') => s
      .toString()
      .toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // strip accents
      .trim();

    const commonAutoProps = {
      multiple: true,
      size: 'small',
      disableCloseOnSelect: true,
      autoHighlight: true,
      isOptionEqualToValue: (o, v) => o?.value === v?.value,
      ListboxProps: { style: { maxHeight: 260 } },
      fullWidth: true,
      ChipProps: { size: 'small' },

      sx: {
        '& .MuiAutocomplete-inputRoot': { flexWrap:'nowrap', minHeight: 40, alignItems:'center' },
        '& .MuiChip-root': { height: 24 }
      },
      renderTags: (value, getTagProps) => {
        const first = value[0];
        const extra = Math.max(0, value.length - 1);
        return (
          <Box sx={{ display:'flex', alignItems:'center', gap:0.5, overflow:'hidden' }}>
            {first && <Chip {...getTagProps({ index: 0 })} key={first.value} label={first.label} size="small" />}
            {extra > 0 && <Chip label={`+${extra}`} size="small" />}
          </Box>
        );
      },

      // radio-look (keep if you like)
      renderOption: (props, option, { selected }) => (
        <li {...props}>
          <Radio checked={selected} sx={{ mr: 1 }} />{option.label}
        </li>
      ),
    };

    return (
      <Collapse in={this.state.filtersOpen} unmountOnExit sx={{ flexShrink: 0 }}>
        <Box
          sx={{
            px: { xs: 0.75, sm: 1 },
            pt: { xs: 0.75, sm: 1.15 },
            pb: { xs: 0.6, sm: 1 },
            bgcolor: '#fff',
            borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
            flexShrink: 0,
          }}
        >
          {/* Quantized grid: perfect alignment across rows */}
          <Box
            sx={{
              display: 'grid',
              gap: { xs: 0.75, sm: 1 },
              gridAutoFlow: 'row dense',
              gridTemplateColumns: {
                xs: 'repeat(auto-fill, minmax(78px, 1fr))',
                sm: 'repeat(auto-fill, 18px)',
              },
              '& .tight': {
                gridColumn: {
                  xs: 'span 1',
                  sm: 'span 5',
                },
                minWidth: 0,
              },
              '& .date': {
                gridColumn: {
                  xs: 'span 2',
                  sm: 'span 7',
                },
                minWidth: 0,
              },
              '& .mid': {
                gridColumn: {
                  xs: 'span 2',
                  sm: 'span 7',
                },
                minWidth: 0,
              },
              '& .prod': {
                gridColumn: {
                  xs: 'span 4',
                  sm: 'span 15',
                },
                minWidth: 0,
              },
              '& .cat': {
                gridColumn: {
                  xs: 'span 2',
                  sm: 'span 10',
                },
                minWidth: 0,
              },
              '& .sub': {
                gridColumn: {
                  xs: 'span 2',
                  sm: 'span 10',
                },
                minWidth: 0,
              },
              '& .MuiInputBase-root': {
                minHeight: {
                  xs: 36,
                  sm: 38,
                },
                borderRadius: '12px',
                fontSize: {
                  xs: '1rem',
                  sm: '0.88rem',
                },
                bgcolor: '#fff',
              },
              '& .MuiInputLabel-root': {
                fontSize: {
                  xs: '0.78rem',
                  sm: '0.84rem',
                },
                whiteSpace: 'nowrap',
              },
              '& .MuiAutocomplete-inputRoot': {
                flexWrap: 'nowrap',
                minHeight: 38,
                alignItems: 'center',
              },
              '& .MuiChip-root': {
                height: 24,
                maxWidth: 120,
                fontSize: '0.72rem',
              },
            }}
          >
            {/* From / To */}
            <Box className="date">
              <TextField
                size="small" fullWidth label="From" type="date" value={dateFrom}
                onChange={(e)=>{ const val=e.target.value; this.setState({ dateFrom: val, dateTo: val }); }}
                InputLabelProps={{shrink:true}}
              />
            </Box>
            <Box className="date">
              <TextField
                size="small" fullWidth label="To" type="date" value={dateTo}
                onChange={(e)=>this.setState({dateTo: e.target.value})}
                InputLabelProps={{shrink:true}}
                helperText={this.state.dateError}
                error={!!this.state.dateError}
              />
            </Box>

            {/* Op / Amount */}
            <Box className="tight">
              <FormControl
                fullWidth size="small"
                error={!!amountError && (this.state.amount_value !== '' || this.state.amount_op !== '')}
              >
                <InputLabel>Op.</InputLabel>
                <Select value={amount_op} label="Op." onChange={(e)=>this.setState({amount_op: e.target.value})}>
                  <MenuItem value=""><em>None</em></MenuItem>
                  <MenuItem value="gt">&gt;</MenuItem>
                  <MenuItem value="lt">&lt;</MenuItem>
                  <MenuItem value="eq">=</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box className="tight">
              <TextField
                size="small" fullWidth label="Amount" type="number"
                inputProps={{ min: 0, step: '1' }}
                value={amount_value}
                onChange={(e)=>this.setState({amount_value: e.target.value})}
                helperText={amountError}
                error={!!amountError && (this.state.amount_value !== '' || this.state.amount_op !== '')}
              />
            </Box>

            {/* Payment */}
            <Box className="mid">
              <Autocomplete
                {...commonAutoProps}
                options={paymentOptions}
                getOptionLabel={(o)=>o?.label || ''}
                value={paymentOptions.filter(opt => (this.state.payment_types||[]).includes(opt.value))}
                onChange={(_, vals) => this.setState({ payment_types: (vals||[]).map(v => v.value) })}
                inputValue=""                 // keep input text empty
                onInputChange={() => {}}      // ignore any attempt to type
                filterOptions={(x) => x}      // don’t filter list based on input
                openOnFocus                   // open list when field gets focus
                /* filterOptions={(options, { inputValue }) => {
                  const q = normalize(inputValue);
                  if (!q) return options;
                  return options.filter(o => normalize(o.label).includes(q));
                }} */
                autoHighlight
                isOptionEqualToValue={(o, v) => o?.value === v?.value}
                // renderInput={(params)=> <TextField {...params} label="Pay" fullWidth />}
                renderInput={(params)=> (
                  <TextField
                    {...params}
                    label="Pay"
                    fullWidth
                    inputProps={{
                      ...params.inputProps,
                      readOnly: true,
                      'aria-readonly': true,
                      style: { width: 1, opacity: 0, padding: 0, border: 0 }, // invisible, not display:none
                    }}
                  />
                )}
                renderTags={(value, getTagProps) => {
                  const first = value[0]; const extra = value.length - 1;
                  return (
                     <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
                      {first && <Chip {...getTagProps({ index: 0 })} label={first.label} size="small" />}
                      {extra > 0 && <Chip label={`+${extra}`} size="small" />}
                    </Box>
                  );
                }}
              />
            </Box>

            {/* Delivery */}
            <Box className="mid">
              <Autocomplete
              {...commonAutoProps}
                options={deliveryOptions}
                getOptionLabel={(o) => o?.label || ''}
                value={deliveryOptions.filter(opt => (this.state.delivery_types || []).includes(opt.value))}
                onChange={(_, vals) => this.setState({ delivery_types: (vals || []).map(v => v.value) })}
                /* filterOptions={(options, { inputValue }) => {
                  const q = normalize(inputValue);
                  if (!q) return options;
                  return options.filter(o => normalize(o.label).includes(q));
                }} */
                inputValue=""                 // keep input text empty
                onInputChange={() => {}}      // ignore any attempt to type
                filterOptions={(x) => x}      // don’t filter list based on input
                openOnFocus                   // open list when field gets focus
                // renderInput={(params) => <TextField {...params} label="Delivery" fullWidth />}
                renderInput={(params)=> (
                  <TextField
                    {...params}
                    label="Delivery"
                    fullWidth
                    inputProps={{
                      ...params.inputProps,
                      readOnly: true,
                      'aria-readonly': true,
                      style: { width: 1, opacity: 0, padding: 0, border: 0 }, // invisible, not display:none
                    }}
                  />
                )}
                renderTags={(value, getTagProps) => {
                  const first = value[0]; const extra = value.length - 1;
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
                      {first && <Chip {...getTagProps({ index: 0 })} label={first.label} size="small" />}
                      {extra > 0 && <Chip label={`+${extra}`} size="small" />}
                    </Box>
                  );
                }}
                isOptionEqualToValue={(o, v) => o?.value === v?.value}
                ListboxProps={{ style: { maxHeight: 260 } }}
                ChipProps={{ size: 'small' }}
                renderOption={(props, option, { selected }) => (
                  <li {...props}><Radio checked={selected} sx={{ mr: 1 }} />{option.label}</li>
                )}
              />
            </Box>

            {/* Product / Category / Subcategory */}
            <Box className="cat">
              <Autocomplete
                {...commonAutoProps}
                options={categoryOptions}
                getOptionLabel={(o)=>o?.label || ''}
                value={categories}
                onChange={this.onCategoriesChange}
                getOptionDisabled={(option) => {
                  const isSelected = !![...(products||[]), ...(categories||[]), ...(subcategories||[])]
                    .find(o => o.value === option.value);
                  return disableNewPicks && !isSelected;
                }}
                renderInput={(params)=> <TextField {...params} label="Category" fullWidth />}
              />
            </Box>
            <Box className="sub">
              <Autocomplete
                {...commonAutoProps}
                options={subcategoryOptions}
                getOptionLabel={(o)=>o?.label || ''}
                value={subcategories}
                onChange={this.onSubcategoriesChange}
                getOptionDisabled={(option) => {
                  const isSelected = !![...(products||[]), ...(categories||[]), ...(subcategories||[])]
                    .find(o => o.value === option.value);
                  return disableNewPicks && !isSelected;
                }}
                renderInput={(params)=> <TextField {...params} label="Subcategory" fullWidth />}
              />
            </Box>
            <Box className="prod">
              <Autocomplete
                {...commonAutoProps}
                options={productOptions}
                getOptionLabel={(o)=>o?.label || ''}
                value={products}
                onChange={this.onProductsChange}
                getOptionDisabled={(option) => {
                  const isSelected = !![...(products||[]), ...(categories||[]), ...(subcategories||[])]
                    .find(o => o.value === option.value);
                  return disableNewPicks && !isSelected;
                }}
                filterOptions={(options, { inputValue }) => {
                  const q = normalize(inputValue);
                  if (!q) return options;
                  return options.filter(o => normalize(o.label).includes(q));
                }}
                renderInput={(params)=> <TextField {...params} label="Product" fullWidth />}
              />
            </Box>
          </Box>

          <Divider sx={{ mt: { xs: 1, sm: 2 } }} />
        </Box>
      </Collapse>
    );
  }

  renderTotalsSummary() {
    const computed = this.computeTotalsFromRows(this.state.rows);
    const backend = this.state.totals || {};

    const filteredTotal =
      Number(backend.orders_filtered) ||
      Number(backend.orders) ||
      computed.orders || 0;

    const periodTotal =
      Number(backend.orders_period_all) ||
      computed.location_total_orders || 0;

    const pct = this.overallPct();

    const Stat = ({ title, value, progress, progressColor }) => (
      <Card
        variant="outlined"
        sx={{
          p: { xs: 1, sm: 1.15 },
          borderRadius: '14px',
          minWidth: 0,
          boxShadow: 'none',
          border: '1px solid rgba(15, 23, 42, 0.06)',
          bgcolor: 'linear-gradient(180deg, #ffffff 0%, #fbfcff 100%)',
        }}
        role="status"
        aria-label={`${title}: ${value}`}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            color="text.secondary"
            noWrap
            title={title}
            sx={{
              display: 'block',
              lineHeight: 1.2,
              mb: 0.35,
              fontSize: {
                xs: '0.72rem',
                sm: '0.78rem',
              },
              fontWeight: 700,
            }}
          >
            {title}
          </Typography>

          <Typography
            sx={{
              lineHeight: 1.05,
              color: '#111827',
              fontWeight: 800,
              fontSize: {
                xs: '1.05rem',
                sm: '1.2rem',
                md: '1.3rem',
              },
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {value}
          </Typography>

          {typeof progress === 'number' && (
            <LinearProgress
              variant="determinate"
              value={progress}
              color={progressColor || this.percentColor(progress)}
              sx={{
                mt: 0.75,
                height: 7,
                borderRadius: 999,
                bgcolor: 'rgba(15, 23, 42, 0.08)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 999,
                },
              }}
              aria-label={`${title} progress`}
            />
          )}
        </Box>
      </Card>
    );

    return (
      <Box
        sx={{
          px: { xs: 0.75, sm: 1 },
          py: { xs: 0.75, sm: 1 },
          flexShrink: 0,
          position: 'sticky',
          top: 53,
          zIndex: 3,
          bgcolor: '#fff',
          borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
          display: 'grid',
          gap: { xs: 0.75, sm: 1 },
          gridTemplateColumns: {
            xs: '1fr 1fr',
            sm: 'repeat(3, minmax(0, 1fr))',
          },
          '& > :last-child': {
            gridColumn: {
              xs: '1 / -1',
              sm: 'auto',
            },
          },
        }}
      >
        <Stat title="Filtered Orders" value={filteredTotal.toLocaleString()} />
        <Stat title="Total Orders" value={periodTotal.toLocaleString()} />
        <Stat title="% of Total" value={`${pct.toFixed(2)}%`} progress={pct} />
      </Box>
    );
  }

  render() {
    const { rows, page, rowsPerPage, loading, error } = this.state;
    const paged = rowsPerPage > 0 ? rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : rows;

    return (
      <Paper
        sx={{
          position: 'relative',
          width: '100%',
          height: {
            xs: 600,
            sm: '100%',
          },
          maxHeight: {
            xs: 600,
            sm: '100%',
          },
          minHeight: 0,
          borderRadius: { xs: '14px', sm: '16px' },
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 4px 14px rgba(15, 23, 42, 0.08)',
          border: '1px solid rgba(15, 23, 42, 0.06)',
          bgcolor: '#fff',
        }}
      >
        <Card
          sx={{
            position: 'relative',
            flex: '1 1 auto',
            minHeight: 0,
            borderRadius: 0,
            display: 'flex',
            flexDirection: 'column',
            boxShadow: 'none',
            overflow: 'hidden',
          }}
        >
          {this.renderFilterBar()}
          {this.renderFilters()}
          {this.renderTotalsSummary()}

          <Box
            sx={{
              flex: '1 1 auto',
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              bgcolor: '#fff',
              overflow: 'hidden',
            }}
          >
              <TableContainer
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflow: 'auto',
                  borderTop: '1px solid rgba(15, 23, 42, 0.06)',
                  '&::-webkit-scrollbar': {
                    height: 8,
                    width: 8,
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(15, 23, 42, 0.18)',
                    borderRadius: 999,
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: 'rgba(15, 23, 42, 0.04)',
                  },
                }}
              >
              <Table
                stickyHeader
                size="small"
                aria-label="orders by location table"
                sx={{
                  minWidth: '100%',
                  width: '100%',
                  tableLayout: {
                    xs: 'fixed',
                    md: 'auto'
                  },
                  '& .MuiTableCell-root': {
                    px: { xs: 0.45, sm: 1 },
                    borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
                    fontSize: {
                      xs: '0.82rem',
                      sm: '0.88rem',
                      md: '0.92rem',
                    },
                  },
                  '& .MuiTableRow-hover:hover': {
                    backgroundColor: 'rgba(26, 35, 126, 0.035)',
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        py: { xs: 0.65, sm: 0.85 },
                        px: { xs: 0.4, sm: 1 },
                        bgcolor: '#f8fafc',
                        color: '#1a237e',
                        fontWeight: 650,
                        fontSize: {
                          xs: '0.88rem',
                          sm: '0.92rem',
                          md: '1rem',
                        },
                        lineHeight: 1.15,
                        whiteSpace: 'nowrap',
                        position: 'sticky',
                        left: 0,
                        zIndex: 4,
                        width: {
                          xs: '36%',
                          sm: 190,
                          md: '36%',
                        },
                      }}
                    >
                      Location
                    </TableCell>

                    <TableCell
                      align="right"
                      sx={{
                        py: { xs: 0.65, sm: 0.85 },
                        px: { xs: 0.4, sm: 1 },
                        bgcolor: '#f8fafc',
                        color: '#1a237e',
                        fontWeight: 650,
                        whiteSpace: 'nowrap',
                        width: { xs: '18%', sm: 140, md: '18%' },
                      }}
                    >
                      Filtered
                    </TableCell>

                    <TableCell
                      align="right"
                      sx={{
                        py: { xs: 0.65, sm: 0.85 },
                        px: { xs: 0.4, sm: 1 },
                        bgcolor: '#f8fafc',
                        color: '#1a237e',
                        fontWeight: 650,
                        whiteSpace: 'nowrap',
                        width: { xs: '18%', sm: 140, md: '18%' },
                      }}
                    >
                      Total
                    </TableCell>

                    <TableCell
                      align="right"
                      sx={{
                        py: { xs: 0.65, sm: 0.85 },
                        px: { xs: 0.4, sm: 1 },
                        bgcolor: '#f8fafc',
                        color: '#1a237e',
                        fontWeight: 650,
                        whiteSpace: 'nowrap',
                        width: { xs: '28%', sm: 210, md: '28%' },
                      }}
                    >
                      % of Total
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {loading && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <LinearProgress />
                      </TableCell>
                    </TableRow>
                  )}

                  {error && !loading && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                            py: 1,
                            color: 'error.main',
                          }}
                        >
                          <Typography sx={{ fontSize: { xs: '0.82rem', sm: '0.9rem' } }}>
                            {error}
                          </Typography>
                          <Button size="small" onClick={this.getData}>
                            Retry
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}

                  {!loading && !error && paged.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Box
                          sx={{
                            py: 4,
                            textAlign: 'center',
                            color: 'text.secondary',
                            fontSize: { xs: '0.86rem', sm: '0.92rem' },
                          }}
                        >
                          No data for the selected filters.
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}

                  {!loading && !error && paged.map((r, idx) => {
                    const pct = Math.min(100, Math.max(0, Number(r.percent_of_total || 0)));

                    return (
                      <TableRow hover key={`row-${page}-${idx}`}>
                        <TableCell
                          sx={{
                            py: { xs: 0.65, sm: 0.8 },
                            px: { xs: 0.75, sm: 1 },
                            position: 'sticky',
                            left: 0,
                            zIndex: 2,
                            bgcolor: '#fff',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontWeight: 550,
                          }}
                        >
                          {r.location_name}
                        </TableCell>

                        <TableCell
                          align="right"
                          sx={{
                            py: { xs: 0.65, sm: 0.8 },
                            px: { xs: 0.75, sm: 1 },
                            whiteSpace: 'nowrap',
                            color: '#111827',
                            fontWeight: 450,
                          }}
                        >
                          {(r.orders ?? 0).toLocaleString()}
                        </TableCell>

                        <TableCell
                          align="right"
                          sx={{
                            py: { xs: 0.65, sm: 0.8 },
                            px: { xs: 0.75, sm: 1 },
                            whiteSpace: 'nowrap',
                            color: '#111827',
                            fontWeight: 450,
                          }}
                        >
                          {r.location_total_orders != null ? r.location_total_orders.toLocaleString() : '–'}
                        </TableCell>

                        <TableCell
                          align="right"
                          sx={{
                            py: { xs: 0.65, sm: 0.8 },
                            px: { xs: 0.75, sm: 1 },
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'flex-end',
                              gap: { xs: 0, sm: 1 },
                            }}
                          >
                            <Box
                              sx={{
                                flex: 1,
                                minWidth: 56,
                                display: {
                                  xs: 'none',
                                  sm: 'block',
                                },
                              }}
                            >
                              <LinearProgress
                                variant="determinate"
                                value={pct}
                                color={this.percentColor(pct)}
                                sx={{
                                  height: 7,
                                  borderRadius: 999,
                                  bgcolor: 'rgba(15, 23, 42, 0.08)',
                                  '& .MuiLinearProgress-bar': {
                                    borderRadius: 999,
                                  },
                                }}
                              />
                            </Box>

                            <Box
                              sx={{
                                width: {
                                  xs: '100%',
                                  sm: 58,
                                },
                                textAlign: 'right',
                                flexShrink: 0,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: {
                                    xs: '0.76rem',
                                    sm: '0.88rem',
                                  },
                                  fontWeight: 550,
                                  color: '#111827',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {pct.toFixed(2)}%
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[10, 25, { value: -1, label: 'All' }]}
              labelRowsPerPage="Rows:"
              component="div"
              count={rows.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={this.handleChangePage}
              onRowsPerPageChange={this.handleChangeRowsPerPage}
              sx={{
                flexShrink: 0,
                borderTop: '1px solid rgba(15, 23, 42, 0.06)',
                bgcolor: '#fff',
                minHeight: { xs: 44, sm: 52 },
                '& .MuiTablePagination-toolbar': {
                  minHeight: { xs: 44, sm: 52 },
                  px: { xs: 0.5, sm: 1 },
                  flexWrap: { xs: 'wrap', sm: 'nowrap' },
                  gap: { xs: 0.25, sm: 0.5 },
                },
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  fontSize: {
                    xs: '0.88rem',
                    sm: '0.92rem',
                    md: '1rem',
                  },
                  m: 0,
                },
                '& .MuiTablePagination-actions': {
                  ml: { xs: 0, sm: 1 },
                },
              }}
            />
          </Box>
        </Card>

        <Snackbar
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          open={this.state.hintOpen}
          autoHideDuration={2200}
          onClose={() => this.setState({ hintOpen: false })}
        >
          <MuiAlert elevation={0} variant="outlined" severity="info" sx={{ bgcolor: 'background.paper' }}>
            <Typography variant="caption">{this.state.hintMsg}</Typography>
          </MuiAlert>
        </Snackbar>

        {this.state.busyOverlay && (
          <Box
            role="status"
            aria-live="polite"
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(15, 23, 42, 0.16)',
              backgroundImage:
                'radial-gradient(ellipse at center, rgba(15, 23, 42, 0.08) 0%, rgba(15, 23, 42, 0.18) 75%, rgba(15, 23, 42, 0.22) 100%)',
              zIndex: 9999,
              pointerEvents: 'auto',
            }}
          >
            <Paper
              elevation={3}
              sx={{
                px: 2.5,
                py: 1.5,
                borderRadius: '14px',
                bgcolor: 'background.paper',
                color: 'text.primary',
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                border: '1px solid rgba(15, 23, 42, 0.08)',
              }}
            >
              <CircularProgress size={22} color="inherit" />
              <Typography variant="body2">Preparing report…</Typography>
            </Paper>
          </Box>
        )}
      </Paper>
    );
  }
}

export default SalesCountOrders;
